import { type Ref } from 'react';

import { ChevronSmallRightDoubleIcon } from '@dynatrace/strato-icons';

import * as styles from './SidebarLayout.sty.js';
import { Button } from '../../../buttons/button/Button.js';
import { Tooltip } from '../../../overlays/tooltip/Tooltip.js';

interface SidebarHint {
  panelId: string;
  expandSidebarLabel: string;
  expandButtonRef: Ref<HTMLButtonElement>;
  /**
   * Called when the user activates the rail to expand the sidebar. Receives
   * the new collapsed state, which is always `false` from this component but
   * matches the controlled-collapse callback shape used elsewhere.
   */
  onCollapsedChange: (collapsed: boolean) => void;
  /**
   * Whether the panel controlled by the expand button is currently collapsed.
   * In `collapsed` mode this is always `true`, but in `drawer` mode the
   * drawer can be open while the rail is still rendered, in which case this
   * must reflect the live state for assistive technologies.
   */
  isCollapsed?: boolean;
}

/**
 * The narrow vertical hint rendered when the sidebar is collapsed (in either
 * the `collapsed` mode or the `drawer` mode while the drawer is dismissed).
 * Suggests to the user that there is a sidebar outside the frame, which can be expanded.
 *
 * @internal
 */
export function SidebarHint({
  panelId,
  expandSidebarLabel,
  expandButtonRef,
  onCollapsedChange,
  isCollapsed = true,
}: SidebarHint) {
  const expand = () => {
    onCollapsedChange(false);
  };

  return (
    <div className={styles.collapsedSidebar} id={panelId}>
      <button
        className={styles.hint}
        type="button"
        onClick={expand}
        tabIndex={-1}
        aria-hidden="true"
        aria-label={expandSidebarLabel}
      />
      <div className={styles.expandButtonContainer}>
        <Tooltip text={expandSidebarLabel}>
          <Button
            className={styles.expandButton}
            aria-expanded={!isCollapsed}
            ref={expandButtonRef}
            aria-controls={panelId}
            aria-label={expandSidebarLabel}
            size="condensed"
            data-dt-children-name="ExpandCollapse"
            data-dt-properties="state:expand"
            onClick={expand}
          >
            <Button.Prefix>
              <ChevronSmallRightDoubleIcon />
            </Button.Prefix>
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}
